import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Download, Smartphone } from "lucide-react";

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after user has been on site for 30 seconds
      setTimeout(() => {
        if (!localStorage.getItem('pwa-prompt-dismissed')) {
          setShowPrompt(true);
        }
      }, 30000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show prompt after delay if not dismissed
    if (iOS && !standalone && !localStorage.getItem('pwa-prompt-dismissed')) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 30000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setShowPrompt(false);
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom-5 duration-300">
      <div className="bg-white rounded-xl shadow-lg border border-[#E5E0D5] p-4 max-w-md mx-auto">
        <div className="flex items-start gap-3">
          <div className="bg-[var(--liturgical-primary)] p-2 rounded-lg">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-[#2D2A26] font-['Crimson_Text']">
              Install Parish App
            </h3>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
              {isIOS 
                ? "Tap the share button and 'Add to Home Screen' to install"
                : "Install for quick access and offline features"
              }
            </p>
            
            {!isIOS && deferredPrompt && (
              <Button
                onClick={handleInstall}
                className="mt-3 bg-[var(--liturgical-primary)] hover:brightness-90 text-white"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Install Now
              </Button>
            )}
            
            {isIOS && (
              <div className="mt-3 text-xs text-[#6B665E] bg-[#F5F1EA] p-2 rounded">
                <strong>iOS:</strong> Tap <span className="inline-block px-1 bg-gray-200 rounded">⎙</span> then "Add to Home Screen"
              </div>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="text-[#6B665E] hover:text-[#2D2A26] p-1"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
